import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Check, ChevronDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useToast } from "@/features/toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTicketDetail, useTicketStats } from "../hooks/use-tickets";
import { TICKET_STATUS_COLORS } from "../config/status-colors";
import { TicketStatusBadge } from "./TicketStatusBadge";

/** Admin'in seçebileceği statüler — backend enum'uyla birebir */
const SELECTABLE_STATUSES = [
  "open",
  "in_progress",
  "on_hold",
  "solved",
  "closed",
] as const;

interface TicketStatusMenuProps {
  className?: string;
}

/**
 * Ticket durum rozeti — admin için tıklanabilir durum menüsüne dönüşür,
 * diğer kullanıcılar için salt-okunur rozet olarak kalır. Ticket verisi
 * URL'deki id üzerinden cache'ten okunur (zero props).
 */
export const TicketStatusMenu: React.FC<TicketStatusMenuProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { success, error: toastError } = useToast();

  const { ticket, updateStatus, isUpdatingStatus } = useTicketDetail(id);
  const { isAdmin } = useTicketStats();

  if (!ticket) return null;

  if (!isAdmin) {
    return <TicketStatusBadge status={ticket.status} className={className} />;
  }

  const handleSelect = async (status: string) => {
    if (status === ticket.status || isUpdatingStatus) return;
    try {
      await updateStatus(status);
      success(t("support.myTickets.statusUpdated"));
    } catch {
      toastError(t("support.myTickets.statusUpdateError"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isUpdatingStatus}>
        <button
          type="button"
          className={cn(
            "group inline-flex shrink-0 items-center gap-1 rounded-full transition-opacity",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50",
            isUpdatingStatus && "pointer-events-none opacity-60",
            className,
          )}
          aria-label={t("support.myTickets.changeStatus")}
        >
          <TicketStatusBadge status={ticket.status} />
          {isUpdatingStatus ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="min-w-[170px] border-border bg-card"
      >
        {SELECTABLE_STATUSES.map((value) => {
          const config = TICKET_STATUS_COLORS[value];
          const isActive = ticket.status === value;
          return (
            <DropdownMenuItem
              key={value}
              onSelect={() => handleSelect(value)}
              className="flex items-center gap-2 text-sm"
            >
              <span
                className={cn("h-2 w-2 shrink-0 rounded-full", config.dot)}
              />
              <span className="flex-1">{t(config.i18nKey)}</span>
              {isActive && <Check className="h-3.5 w-3.5 text-indigo-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
